import { useMemo, useRef, useEffect } from "react";
import { MapPin, CheckCircle2, Navigation, Car, Clock } from "lucide-react";

/**
 * TripStrip — horizontal "trip strip" replacement for the vertical timeline.
 *
 * Renders each leg (Pickup → Stop 1 → ... → Dropoff) as a card in a
 * horizontally-scrollable row. Cards are connected by a progress line that
 * fills as the driver advances through en_route_X → at_X phases.
 *
 * Props:
 *   - booking: full booking object (needs status/phase/pickup_address/dropoff_address/stops)
 *
 * Data-testid roots:
 *   - `trip-strip` (root)
 *   - `trip-strip-leg-{key}` on each card
 *   - `trip-strip-callout` for the "next up" callout below
 */
export default function TripStrip({ booking }) {
  const scrollRef = useRef(null);

  const legs = useMemo(() => {
    if (!booking) return [];
    const stops = Array.isArray(booking.stops) ? booking.stops : [];
    const rows = [
      {
        key: "pickup",
        label: "Pickup",
        address: booking.pickup_address || "",
        icon: MapPin,
        accent: "emerald",
      },
      ...stops.map((s, i) => ({
        key: `stop_${i}`,
        label: `Stop ${i + 1}`,
        address: s?.address || "",
        icon: MapPin,
        accent: "amber",
      })),
      {
        key: "dropoff",
        label: "Dropoff",
        address: booking.dropoff_address || "",
        icon: CheckCircle2,
        accent: "sky",
      },
    ];
    return rows;
  }, [booking]);

  // Resolve the "phase" for each leg: upcoming | en_route | at | done
  const { statuses, activeIndex, isComplete } = useMemo(() => {
    const phase = booking?.phase || "";
    const status = booking?.status || "";
    const completed = status === "completed" || phase === "completed";

    // Find which leg key we're "at" or "en route to"
    let active = -1;
    let activeType = null; // "en_route" | "at"
    for (let i = 0; i < legs.length; i++) {
      const k = legs[i].key;
      if (phase === `en_route_${k}`) {
        active = i;
        activeType = "en_route";
        break;
      }
      if (phase === `at_${k}`) {
        active = i;
        activeType = "at";
        break;
      }
    }

    const st = legs.map((_, i) => {
      if (completed) return "done";
      if (active === -1) return "upcoming";
      if (i < active) return "done";
      if (i === active) return activeType === "at" ? "at" : "en_route";
      return "upcoming";
    });

    return { statuses: st, activeIndex: active, isComplete: completed };
  }, [booking?.phase, booking?.status, legs]);

  // Auto-scroll the active card into view whenever it changes
  useEffect(() => {
    if (activeIndex < 0 || !scrollRef.current) return;
    const el = scrollRef.current.querySelector(`[data-leg-index="${activeIndex}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
  }, [activeIndex]);

  if (legs.length === 0) return null;

  // Progress line fill (percent) — spans left edge of first card to right edge
  // of last card. Each "done" leg contributes 1/(N-1), the active leg
  // contributes 0.5/(N-1) when at "at" state (halfway), 0.25 when en_route.
  const progressPct = (() => {
    if (isComplete) return 100;
    if (activeIndex < 0) return 0;
    const doneCount = statuses.filter((s) => s === "done").length;
    const partial = statuses[activeIndex] === "at" ? 0.75 : 0.35;
    const total = legs.length - 1 || 1;
    return Math.min(100, ((doneCount + partial) / total) * 100);
  })();

  // Shared per-leg visual computations — used by both the mobile stacked
  // list and the desktop horizontal strip so we keep a single source of
  // truth for state → colors/labels/icons.
  const legVisuals = legs.map((leg, i) => {
    const state = statuses[i];
    const isEnRoute = state === "en_route";
    const isAt = state === "at";
    const isDone = state === "done";
    const isActive = isEnRoute || isAt;
    const iconEl = isEnRoute ? Car : isDone ? CheckCircle2 : leg.icon;
    const pillLabel = isEnRoute
      ? "On the way"
      : isAt
      ? "Arrived"
      : isDone
      ? "Complete"
      : "Upcoming";
    const pillStyle = isEnRoute
      ? "bg-[#0047FF]/20 text-[#7AADFF] border-[#0047FF]/40"
      : isAt
      ? "bg-amber-500/15 text-amber-300 border-amber-500/30"
      : isDone
      ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30"
      : "bg-white/5 text-zinc-500 border-white/10";
    const iconBubble = isActive
      ? "bg-[#0047FF] text-white ring-4 ring-[#0047FF]/20"
      : isDone
      ? "bg-emerald-500 text-white"
      : "bg-white/5 text-zinc-500";
    const cardStyle = isActive
      ? "bg-gradient-to-b from-[#0f2140] to-[#0a1428] border-[#0047FF]/60 shadow-[0_0_0_1px_rgba(0,71,255,0.35),0_10px_30px_-10px_rgba(0,71,255,0.5)]"
      : isDone
      ? "bg-[#0f1e15] border-emerald-500/30"
      : "bg-white/[0.03] border-white/5";
    return { state, isEnRoute, isAt, isDone, isActive, iconEl, pillLabel, pillStyle, iconBubble, cardStyle };
  });

  const renderPillIcon = (state) => {
    if (state === "en_route") return <Navigation className="w-2.5 h-2.5" />;
    if (state === "at") return <MapPin className="w-2.5 h-2.5" />;
    if (state === "done") return <CheckCircle2 className="w-2.5 h-2.5" />;
    return <Clock className="w-2.5 h-2.5" />;
  };

  return (
    <div data-testid="trip-strip" className="space-y-3">
      {/* Mobile: vertical stacked list — one row per leg with connecting
          rail on the left. No horizontal scroll, no clipping on phones. */}
      <ol
        className="sm:hidden relative space-y-2 pl-1"
        data-testid="trip-strip-mobile"
      >
        {legs.map((leg, i) => {
          const v = legVisuals[i];
          const IconEl = v.iconEl;
          const isLast = i === legs.length - 1;
          return (
            <li
              key={leg.key}
              data-leg-index={i}
              data-testid={`trip-strip-leg-${leg.key}`}
              className={`relative flex items-center gap-3 rounded-2xl border px-3 py-2.5 ${v.cardStyle}`}
            >
              {/* Connecting rail segment beneath the icon */}
              {!isLast && (
                <span
                  aria-hidden="true"
                  className={`absolute left-[26px] top-[52px] bottom-[-10px] w-0.5 rounded-full ${
                    v.isDone ? "bg-emerald-500/60" : "bg-white/10"
                  }`}
                />
              )}
              <div
                className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${v.iconBubble} ${
                  v.isEnRoute ? "animate-pulse" : ""
                }`}
              >
                <IconEl className="w-4 h-4" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-[10px] uppercase tracking-wider text-zinc-500">{leg.label}</p>
                  <span
                    className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium flex-shrink-0 ${v.pillStyle}`}
                    data-testid={`trip-strip-pill-${leg.key}`}
                  >
                    {renderPillIcon(v.state)}
                    {v.pillLabel}
                  </span>
                </div>
                <p className="text-xs text-white truncate mt-0.5">
                  {leg.address ? leg.address.split(",")[0] : "—"}
                </p>
              </div>
            </li>
          );
        })}
      </ol>

      {/* Tablet / desktop: original horizontal strip with progress rail */}
      <div
        ref={scrollRef}
        className="hidden sm:block relative overflow-x-auto scrollbar-none -mx-1 px-1 pb-2"
        style={{ scrollbarWidth: "none" }}
        data-testid="trip-strip-desktop"
      >
        {/* Connecting rail (behind cards) */}
        <div className="absolute left-6 right-6 top-[38px] h-1 rounded-full bg-white/5 z-0 pointer-events-none" />
        <div
          className="absolute left-6 top-[38px] h-1 rounded-full bg-gradient-to-r from-[#0047FF] to-[#4D88FF] z-0 pointer-events-none transition-all duration-700 ease-out"
          style={{ width: `calc((100% - 48px) * ${progressPct / 100})` }}
        />

        <div className="relative z-10 flex items-stretch gap-3 min-w-max">
          {legs.map((leg, i) => {
            const v = legVisuals[i];
            const IconRender = v.iconEl;

            return (
              <div
                key={leg.key}
                data-leg-index={i}
                data-testid={`trip-strip-leg-desktop-${leg.key}`}
                className={`relative flex-none w-[180px] rounded-2xl border p-3 transition-all duration-300 ${v.cardStyle}`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <div
                    className={`w-9 h-9 rounded-full flex items-center justify-center ${v.iconBubble} ${
                      v.isEnRoute ? "animate-pulse" : ""
                    }`}
                  >
                    <IconRender className="w-4 h-4" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] uppercase tracking-wider text-zinc-500">{leg.label}</p>
                    <p className="text-xs text-white truncate">
                      {leg.address ? leg.address.split(",")[0] : "—"}
                    </p>
                  </div>
                </div>
                <span
                  className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium ${v.pillStyle}`}
                  data-testid={`trip-strip-pill-desktop-${leg.key}`}
                >
                  {renderPillIcon(v.state)}
                  {v.pillLabel}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Next-up callout */}
      <div
        className="rounded-xl bg-[#121212] border border-white/5 px-3 py-2.5 flex items-center gap-2.5"
        data-testid="trip-strip-callout"
      >
        {isComplete ? (
          <>
            <CheckCircle2 className="w-4 h-4 text-emerald-400" />
            <p className="text-xs text-emerald-200">Trip complete — thanks for riding with Link Ride.</p>
          </>
        ) : activeIndex < 0 ? (
          <>
            <Clock className="w-4 h-4 text-zinc-400" />
            <p className="text-xs text-zinc-300">
              Waiting for your driver to start the trip.
            </p>
          </>
        ) : statuses[activeIndex] === "at" ? (
          <>
            <MapPin className="w-4 h-4 text-amber-300" />
            <p className="text-xs text-white">
              <span className="text-amber-300 font-semibold">Arrived</span> at {legs[activeIndex].label.toLowerCase()}.
            </p>
          </>
        ) : (
          <>
            <Car className="w-4 h-4 text-[#7AADFF]" />
            <p className="text-xs text-white">
              Driver is <span className="text-[#7AADFF] font-semibold">en route</span> to {legs[activeIndex].label.toLowerCase()}.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
